import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { Stars } from "./Stars";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { customerApi } from "../lib/customerApi";
import { api, formatApiErrorDetail } from "../lib/api";

export function Reviews({ slug, productId, refreshProduct }) {
    const { user } = useCustomerAuth();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ rating: 5, title: "", body: "" });
    const [busy, setBusy] = useState(false);

    const refresh = async (p = page) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/products/${slug}/reviews`, { params: { page: p, limit: 10 } });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setPage(p);
        } catch (_e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(1); /* eslint-disable-next-line */ }, [slug]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.body.trim()) {
            toast.error("Please share a few words about the piece");
            return;
        }
        setBusy(true);
        try {
            await customerApi.post(`/products/${slug}/reviews`, form);
            toast.success("Review posted — thank you!");
            setForm({ rating: 5, title: "", body: "" });
            refresh(1);
            refreshProduct?.();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not post review");
        } finally {
            setBusy(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / 10));

    return (
        <section className="border-t border-inkDark/5 mt-16 pt-12" data-testid="reviews-section">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <span className="label-eyebrow">Reviews</span>
                    <h2 className="mt-2 font-display text-3xl text-inkDark tracking-tight">From the atelier&rsquo;s clients</h2>
                </div>
                <div className="text-inkMuted text-sm">{total} {total === 1 ? "review" : "reviews"}</div>
            </div>

            {loading ? (
                <div className="mt-8 text-inkMuted">Loading reviews…</div>
            ) : items.length === 0 ? (
                <div className="mt-8 bg-beige rounded-2xl p-6 text-inkMuted" data-testid="reviews-empty">
                    Be the first to share your experience with this piece.
                </div>
            ) : (
                <div className="mt-8 space-y-5" data-testid="reviews-list">
                    {items.map((r) => (
                        <article key={r.id} className="bg-ivory border border-inkDark/5 rounded-2xl p-5" data-testid={`review-${r.id}`}>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-3">
                                    <span className="w-9 h-9 rounded-full bg-skyblue/40 flex items-center justify-center text-sm font-medium text-inkDark">
                                        {(r.userName || "U")[0].toUpperCase()}
                                    </span>
                                    <div>
                                        <div className="text-inkDark font-medium">{r.userName || "Anonymous"}</div>
                                        <div className="text-xs text-inkMuted">{new Date(r.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
                                    </div>
                                </div>
                                <Stars rating={r.rating} size={14} />
                            </div>
                            {r.title ? <div className="mt-3 font-serif text-lg text-inkDark">{r.title}</div> : null}
                            <p className="mt-2 text-inkDark/85 leading-relaxed whitespace-pre-line">{r.body}</p>
                        </article>
                    ))}
                </div>
            )}

            {totalPages > 1 ? (
                <div className="mt-6 flex items-center gap-2 justify-center" data-testid="reviews-pagination">
                    <button onClick={() => refresh(Math.max(1, page - 1))} disabled={page <= 1} className="px-3 py-1.5 rounded-full border border-inkDark/10 text-sm disabled:opacity-30">Prev</button>
                    <span className="text-sm text-inkMuted">Page {page} of {totalPages}</span>
                    <button onClick={() => refresh(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="px-3 py-1.5 rounded-full border border-inkDark/10 text-sm disabled:opacity-30">Next</button>
                </div>
            ) : null}

            <div className="mt-12 bg-beige rounded-2xl p-6 lg:p-8" data-testid="review-form-block">
                <span className="label-eyebrow">Share your thoughts</span>
                <h3 className="mt-2 font-display text-2xl text-inkDark">Write a review</h3>
                {user ? (
                    <form onSubmit={submit} className="mt-5 space-y-4" data-testid="review-form">
                        <div className="flex items-center gap-1" data-testid="review-rating-picker">
                            {[1, 2, 3, 4, 5].map((s) => (
                                <button
                                    type="button"
                                    key={s}
                                    onClick={() => setForm({ ...form, rating: s })}
                                    className="p-1"
                                    aria-label={`${s} stars`}
                                    data-testid={`review-rating-${s}`}
                                >
                                    <Star size={22} className={s <= form.rating ? "fill-skyblueDeep text-skyblueDeep" : "text-inkDark/20"} />
                                </button>
                            ))}
                        </div>
                        <input
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="Title (optional)"
                            className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5 outline-none focus:border-skyblueDeep"
                            data-testid="review-title-input"
                        />
                        <textarea
                            required
                            rows={4}
                            value={form.body}
                            onChange={(e) => setForm({ ...form, body: e.target.value })}
                            placeholder="Tell us about the fabric, fit, finish…"
                            className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5 outline-none focus:border-skyblueDeep"
                            data-testid="review-body-input"
                        />
                        <div className="flex justify-end">
                            <button type="submit" disabled={busy} className="btn-pill text-sm disabled:opacity-50" data-testid="review-submit-button">
                                {busy ? "Posting…" : "Post review"}
                            </button>
                        </div>
                    </form>
                ) : (
                    <p className="mt-4 text-inkMuted">
                        <Link to={`/login?next=/product/${slug}`} className="text-skyblueDeep underline" data-testid="review-signin-link">
                            Sign in
                        </Link>{" "}
                        to write a review.
                    </p>
                )}
            </div>
        </section>
    );
}
